// SEO Component using React 19 native document metadata hoisting
// React 19 automatically hoists <title>, <meta>, <link> tags to <head>

import { SITE, getFullUrl, getImageUrl } from "./constants";

interface SEOProps {
  readonly title: string;
  readonly description: string;
  readonly canonical?: string;
  readonly ogType?: "website" | "article" | "product";
  readonly ogImage?: string;
  readonly noIndex?: boolean;
  readonly jsonLd?: object | object[];
}

// Helper to normalize jsonLd to array
function normalizeSchemas(jsonLd: object | object[] | undefined): object[] {
  if (!jsonLd) return [];
  return Array.isArray(jsonLd) ? jsonLd : [jsonLd];
}

export function SEO({
  title,
  description,
  canonical,
  ogType = "website",
  ogImage,
  noIndex = false,
  jsonLd,
}: SEOProps) {
  const fullTitle = title.includes(SITE.name) ? title : `${title} | ${SITE.name}`;
  const canonicalUrl = canonical ? getFullUrl(canonical) : undefined;
  const imageUrl = getImageUrl(ogImage || SITE.logo);
  const schemas = normalizeSchemas(jsonLd);

  return (
    <>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      
      {/* Robots */}
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta
          name="robots"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
      )}

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:locale" content={SITE.locale} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content={SITE.twitter} />

      {/* JSON-LD Structured Data */}
      {schemas.map((schema) => {
        const schemaType = (schema as { "@type"?: string })["@type"] || "unknown";
        const key = `schema-${schemaType}-${JSON.stringify(schema).length}`;
        return (
          <script
            key={key}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />
        );
      })}
    </>
  );
}
